import { CircularProgress, List, Paper, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { individualBalances } from '../api';
import { OrderItem } from '../components/OrderItem';
import { AuthContext } from '../context/AuthContext';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { IMemberSalesOrderInfoResult } from '../utils/types';
import { BalancesQueryDialog } from './BalancesQueryDialog';

type OrdersContainerProps = {
  balancesQueryDialogOpen: boolean;
  setBalancesQueryDialogOpen: (open: boolean) => void;
};

export const OrdersContainer = ({
  balancesQueryDialogOpen,
  setBalancesQueryDialogOpen,
}: OrdersContainerProps) => {
  const { token } = useContext(AuthContext);
  const [orders, setOrders] = useState<IMemberSalesOrderInfoResult[]>();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const showSnackbar = useAlertSnackbar();
  useEffect(() => {
    fetchBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBalances = async () => {
    setSubmitting(true);
    try {
      const orders = await individualBalances(false, token);
      const sortedOrders = orders.sort((a, b) => {
        return (
          new Date(b.tourStartDate).getTime() -
          new Date(a.tourStartDate).getTime()
        );
      });

      setOrders(sortedOrders);
    } catch (e: any) {
      showSnackbar(e.detail, 'error');
      setSubmitting(false);
    }
    setSubmitting(false);
  };

  if (submitting) {
    return <CircularProgress color="secondary" />;
  }

  return (
    <Paper>
      {orders?.length == 0 && !submitting && (
        <Typography>{t('ordersContainer.noRecord')}</Typography>
      )}
      <List>
        {orders?.map((balance) => (
          <OrderItem
            key={`orderitems-${balance.salesIndividualId}`}
            salesIndividualId={balance.salesIndividualId.toString()}
            number={balance.orderNumber}
            tourName={balance.tourInfo}
            customerLastName={balance.customerLastName}
            orderNumber={balance.orderNumber}
            tourId={balance.tourId}
            tourEndDate={balance.tourEndDate}
            tourStartDate={new Date(balance.tourStartDate)
              .toLocaleString('tr-TR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              } as Intl.DateTimeFormatOptions)
              .toString()}
          />
        ))}
      </List>
      {balancesQueryDialogOpen && (
        <BalancesQueryDialog
          open={true}
          onClose={() => setBalancesQueryDialogOpen(false)}
        />
      )}
    </Paper>
  );
};
